import React, { useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Upload, Loader2, ImagePlus, X, Wand2, Undo2, Sparkles } from 'lucide-react';

export const STYLE_OPTIONS = [
  { value: 'watercolor', label: 'Watercolor', hint: 'Soft, painterly, editorial' },
  { value: 'editorial', label: 'Editorial', hint: 'Semi-flat, magazine cover feel' },
  { value: 'minimal', label: 'Minimal', hint: 'Apple-like, white space, single subject' },
  { value: 'bold', label: 'Bold', hint: 'Modern, high contrast, one accent' },
];

export const HISTORY_LIMIT = 3;

// Push current cover into history (front). Keep top HISTORY_LIMIT unique entries.
const stashCover = (v) => {
  if (!v.cover_image) return v.cover_history || [];
  const prev = v.cover_history || [];
  if (prev[0] === v.cover_image) return prev;
  return [v.cover_image, ...prev.filter((u) => u !== v.cover_image)].slice(0, HISTORY_LIMIT);
};

/**
 * Reusable cover image block: upload / drop-zone, AI generation with style picker,
 * last-3 cover history and AI alt-text. `value` = { cover_image, cover_history, alt_text },
 * `context` = { title, excerpt, tags } used to compose the artwork and alt-text.
 */
export const CoverImagePanel = ({ value, onChange, context }) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [suggestingAlt, setSuggestingAlt] = useState(false);
  const [style, setStyle] = useState('watercolor');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const history = value.cover_history || [];
  const styleLabel = STYLE_OPTIONS.find((s) => s.value === style)?.label || 'Illustration';

  const setCover = (url) => onChange({ cover_history: stashCover(value), cover_image: url });

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast({ title: 'Only image files are supported', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Image is larger than 5MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCover(res.data.url);
      toast({ title: 'Cover image uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const suggestAltText = async ({ silent } = {}) => {
    if (!context.title?.trim()) {
      if (!silent) toast({ title: 'Add a title first', description: 'The alt-text is composed from the title, excerpt and tags.', variant: 'destructive' });
      return;
    }
    setSuggestingAlt(true);
    try {
      const res = await api.post('/admin/blog/suggest-alt-text', { title: context.title, excerpt: context.excerpt || '', tags: context.tags || [] });
      onChange({ alt_text: res.data.alt_text });
      if (!silent) toast({ title: 'Alt-text suggested', description: 'Edit it to fit the exact image if you like.' });
    } catch (err) {
      if (!silent) toast({ title: 'Suggestion failed', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSuggestingAlt(false);
    }
  };

  const generateWithAI = async () => {
    if (!context.title?.trim()) {
      toast({ title: 'Add a title first', description: 'The AI uses the post title to compose the artwork.', variant: 'destructive' });
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post('/admin/blog/generate-cover', { title: context.title, excerpt: context.excerpt || '', tags: context.tags || [], style });
      setCover(res.data.url);
      toast({ title: 'Cover generated', description: `${styleLabel} created from your title.` });
      if (!value.alt_text?.trim()) void suggestAltText({ silent: true });
    } catch (err) {
      toast({ title: 'Generation failed', description: err.response?.data?.detail || 'The image service is temporarily unavailable.', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const revertToHistory = (url, idx) => {
    const newHistory = history.filter((_, i) => i !== idx);
    const withCurrent = value.cover_image && value.cover_image !== url ? [value.cover_image, ...newHistory].slice(0, HISTORY_LIMIT) : newHistory;
    onChange({ cover_image: url, cover_history: withCurrent });
    toast({ title: 'Reverted to previous cover' });
  };

  const busyLabel = `Generating ${styleLabel.toLowerCase()} cover…`;

  return (
    <div data-testid="cover-image-panel">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-[11px] uppercase tracking-wider text-ink-3">Cover image</Label>
        {value.cover_image && (
          <button type="button" onClick={() => setCover('')} className="text-[11px] text-ink-3 hover:text-red-600 inline-flex items-center gap-1" data-testid="cover-remove-btn">
            <X className="w-3 h-3" /> Remove
          </button>
        )}
      </div>

      {value.cover_image ? (
        <div className="relative group">
          <img src={value.cover_image} alt={value.alt_text || 'Post cover'} className={`w-full max-h-80 object-cover rounded-xl border border-[var(--line-2)] ${generating ? 'opacity-40' : ''}`} />
          {generating ? (
            <div className="absolute inset-0 flex items-center justify-center rounded-xl">
              <div className="bg-white/95 backdrop-blur px-4 py-2 rounded-full text-sm text-ink inline-flex items-center gap-2 shadow-sm"><Loader2 className="w-4 h-4 animate-spin" /> {busyLabel}</div>
            </div>
          ) : (
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded-xl flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <button type="button" onClick={() => fileInputRef.current?.click()} className="btn-invert" disabled={uploading}>
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Replace</>}
              </button>
              <button type="button" onClick={generateWithAI} className="btn-invert-outline"><Wand2 className="w-4 h-4" /> Regenerate</button>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files?.[0]); }}
          disabled={generating}
          data-testid="cover-dropzone"
          className={`w-full aspect-[16/7] rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-colors ${dragOver ? 'border-ink bg-[var(--bg-alt)]' : 'border-[var(--line)] hover:border-ink hover:bg-[var(--bg-alt)]'} ${generating ? 'opacity-70' : ''}`}
        >
          {generating ? (
            <><Loader2 className="w-6 h-6 text-ink animate-spin" /><div className="text-sm text-ink font-medium">{busyLabel}</div><div className="text-xs text-ink-3">About 10–20 seconds</div></>
          ) : uploading ? (
            <><Loader2 className="w-6 h-6 text-ink animate-spin" /><div className="text-sm text-ink">Uploading…</div></>
          ) : (
            <><ImagePlus className="w-6 h-6 text-ink-3" /><div className="text-sm text-ink font-medium">Drop an image or click to upload</div><div className="text-xs text-ink-3">PNG, JPG, or WebP · up to 5MB</div></>
          )}
        </button>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="w-44">
          <Select value={style} onValueChange={setStyle} disabled={generating}>
            <SelectTrigger className="rounded-lg border-[var(--line-2)] text-sm" data-testid="cover-style-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STYLE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  <div className="flex flex-col text-left"><span className="text-sm font-medium text-ink">{o.label}</span><span className="text-[10px] text-ink-3">{o.hint}</span></div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button type="button" onClick={generateWithAI} disabled={generating || uploading} className="btn-primary disabled:opacity-60" data-testid="cover-generate-btn">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Wand2 className="w-4 h-4" /> Generate cover with AI</>}
        </button>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => doUpload(e.target.files?.[0])} className="hidden" data-testid="cover-file-input" />
      <Input value={value.cover_image || ''} onChange={(e) => onChange({ cover_image: e.target.value })} className="mt-3 rounded-lg border-[var(--line-2)] text-xs" placeholder="or paste an image URL" data-testid="cover-url-input" />

      {history.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider text-ink-3 mb-2 flex items-center gap-1.5"><Undo2 className="w-3 h-3" /> Previous covers ({history.length}/{HISTORY_LIMIT})</div>
          <div className="grid grid-cols-3 gap-2">
            {history.map((url, idx) => (
              <div key={idx} className="relative group">
                <button type="button" onClick={() => revertToHistory(url, idx)} className="block w-full aspect-[16/10] rounded-lg overflow-hidden border border-[var(--line-2)] hover:border-ink transition-colors" title="Revert to this cover">
                  <img src={url} alt="Previous cover" className="w-full h-full object-cover" />
                </button>
                <button type="button" onClick={() => onChange({ cover_history: history.filter((_, i) => i !== idx) })} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 border border-[var(--line-2)] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Remove from history">
                  <X className="w-3 h-3 text-ink" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-[11px] uppercase tracking-wider text-ink-3">Alt text (for accessibility)</Label>
          <button type="button" onClick={() => suggestAltText()} disabled={suggestingAlt} className="text-[11px] text-ink hover:text-ink-2 inline-flex items-center gap-1 disabled:opacity-50" data-testid="cover-alt-suggest-btn">
            {suggestingAlt ? <><Loader2 className="w-3 h-3 animate-spin" /> Suggesting…</> : <><Sparkles className="w-3 h-3" /> Suggest with AI</>}
          </button>
        </div>
        <Input value={value.alt_text || ''} onChange={(e) => onChange({ alt_text: e.target.value })} className="rounded-lg border-[var(--line-2)]" placeholder="Describe the visual metaphor in one short sentence…" data-testid="cover-alt-input" />
        <div className="text-[10px] text-ink-3 mt-1">Shown to screen readers and when the image can&apos;t load. Auto-suggested after each AI generation.</div>
      </div>
    </div>
  );
};

export default CoverImagePanel;
